/**
 * O tempo de render dos dois lados, para o painel mostrar a comparação.
 *
 * Enquanto o ProofScope e este viewer convivem, o painel do arquivo no manager
 * mostra quanto cada um levou com o mesmo arquivo (até 03/set era o painel
 * técnico do viewer; o produto pediu que saísse de lá). São réguas diferentes
 * e vale saber disso:
 * o número do ProofScope é wall clock do worker dele, e o daqui é o tempo de
 * trabalho do render, sem a fila nem o envelope do Celery.
 */

/** Campos de `render.timings` que valem como tempo de trabalho, em ordem. */
const CAMPOS_EM_SEGUNDOS = ["service_total_seconds", "render_s"] as const;
const CAMPOS_EM_MILISSEGUNDOS = ["render_ms", "viewer_adapter_total_ms"] as const;

function numeroPositivo(valor: unknown): number | null {
  const numero = typeof valor === "number" ? valor : Number.NaN;
  return Number.isFinite(numero) && numero >= 0 ? numero : null;
}

/** O tempo de render deste viewer, do campo mais específico ao menos. */
export function segundosDoRender(timings: Record<string, unknown> | null | undefined): number | null {
  if (!timings) return null;
  for (const campo of CAMPOS_EM_SEGUNDOS) {
    const valor = numeroPositivo(timings[campo]);
    if (valor !== null) return valor;
  }
  for (const campo of CAMPOS_EM_MILISSEGUNDOS) {
    const valor = numeroPositivo(timings[campo]);
    if (valor !== null) return valor / 1000;
  }
  return null;
}

/** `5,19 s` — segundo com vírgula, como o resto da interface. */
export function formatarSegundos(segundos: number | null | undefined): string | null {
  if (segundos === null || segundos === undefined || !Number.isFinite(segundos) || segundos < 0) {
    return null;
  }
  const casas = segundos < 10 ? 2 : 1;
  return `${segundos.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  })} s`;
}

/**
 * Quanto este viewer foi mais rápido (ou mais lento) que o ProofScope.
 *
 * Devolve null quando falta um dos lados ou quando o ProofScope marcou zero —
 * dividir por ele daria um infinito que não significa nada.
 */
export function comparacaoDeRender(nosso: number | null, deles: number | null): string | null {
  if (nosso === null || deles === null || deles <= 0) return null;
  const razao = nosso / deles;
  if (razao >= 0.97 && razao <= 1.03) return "equivalente";
  if (razao < 1) {
    return `${(1 / razao).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}× mais rápido`;
  }
  return `${razao.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}× mais lento`;
}
